import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { chatApi, type ChatMessage, type ChatMode, type SavedFact } from '../api/chat'
import { insightCategoryLabel } from '../api/insights'
import { ApiError } from '../lib/api'
import { formatDateTimeBR } from '../lib/format'
import { Spinner } from './Spinner'
import { ExportPdfButton } from './ExportPdfButton'

// Mensagem na tela: o que a IA gravou no dossiê vem junto da resposta.
type Bubble = ChatMessage & { saved?: string[] }

const INTERVIEW_OPENER = 'Vamos começar a entrevista sobre este cliente.'

/**
 * Conversa reaberta: cada fato gravado volta para baixo da resposta da IA que o salvou
 * (a 1ª resposta depois dele — o fato é gravado segundos antes da resposta).
 */
function withSavedFacts(
  messages: Array<ChatMessage & { createdAt: string }>,
  facts: SavedFact[]
): Bubble[] {
  const out: Bubble[] = messages.map((m) => ({ role: m.role, content: m.content }))
  for (const f of facts) {
    const i = messages.findIndex((m) => m.role === 'assistant' && m.createdAt >= f.createdAt)
    if (i < 0) continue
    const off = f.status === 'rejected' || !f.active ? ' (desativado depois)' : ''
    ;(out[i].saved ??= []).push(`${insightCategoryLabel(f.category)}: ${f.content}${off}`)
  }
  return out
}

/**
 * Painel de chat reutilizável. As conversas são SALVAS no servidor (por usuário +
 * cliente, ou + relatório): ao abrir, continua a mais recente; dá para abrir as
 * anteriores ou começar outra. Troque `startToken` para começar uma conversa nova
 * (com `newMode="interview"`, a IA já abre a entrevista do dossiê).
 */
export function ChatPanel({
  clientId,
  reportId,
  newMode = 'chat',
  startToken = 0,
  openConversationId,
  disabled,
  emptyHint,
  className,
  exportTitle = 'Conversa com a IA',
  exportSubtitle,
}: {
  clientId: string
  reportId?: string
  newMode?: ChatMode
  startToken?: number
  /** Abre esta conversa (do próprio usuário) em vez da mais recente. */
  openConversationId?: string
  disabled?: boolean
  emptyHint: React.ReactNode
  className?: string
  /** Título/contexto do PDF exportado com a conversa. */
  exportTitle?: string
  exportSubtitle?: string
}) {
  const qc = useQueryClient()
  const [messages, setMessages] = useState<Bubble[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [mode, setMode] = useState<ChatMode>(newMode)
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  // Escopo já inicializado (evita reabrir a conversa mais recente a cada refetch).
  const initializedFor = useRef<string | null>(null)
  const scopeKey = `${clientId}:${reportId ?? ''}:${startToken}`

  const listKey = ['chat-conversations', clientId, reportId ?? '']
  const { data: listData, isSuccess: listLoaded } = useQuery({
    queryKey: listKey,
    queryFn: () => chatApi.conversations(clientId, reportId),
    enabled: !!clientId && !disabled,
  })
  const conversations = listData?.conversations ?? []

  // Conversa e modo vão como VARIÁVEIS da mutation (não do closure): ao começar uma
  // entrevista o estado ainda não atualizou, e a abertura cairia na conversa antiga.
  const send = useMutation({
    mutationFn: (v: { text: string; conversationId: string | null; mode: ChatMode }) => {
      const body = { conversationId: v.conversationId, message: v.text, mode: v.mode }
      return reportId ? chatApi.sendForReport(reportId, body) : chatApi.send(clientId, body)
    },
    onSuccess: (res) => {
      setConversationId(res.conversationId)
      setMessages((m) => [...m, { role: 'assistant', content: res.reply, saved: res.saved }])
      qc.invalidateQueries({ queryKey: listKey })
      // Toda resposta conta no histórico de entrevistas (respostas dadas, fatos salvos).
      qc.invalidateQueries({ queryKey: ['client-interviews'] })
      if (res.saved.length) {
        qc.invalidateQueries({ queryKey: ['client-insights'] })
        qc.invalidateQueries({ queryKey: ['knowledge'] })
      }
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Erro ao enviar mensagem')
      // Devolve a pergunta ao campo para o usuário tentar de novo.
      setMessages((m) => {
        const last = m[m.length - 1]
        if (last?.role === 'user') {
          if (last.content !== INTERVIEW_OPENER) setInput(last.content)
          return m.slice(0, -1)
        }
        return m
      })
    },
  })

  const open = useMutation({
    mutationFn: (id: string) => chatApi.conversation(id),
    onSuccess: (res) => {
      setConversationId(res.conversation.id)
      setMode(res.conversation.mode)
      setMessages(withSavedFacts(res.messages, res.savedFacts))
      setError(null)
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => chatApi.remove(id),
    onSuccess: () => {
      startNew('chat')
      qc.invalidateQueries({ queryKey: listKey })
    },
  })

  function startNew(m: ChatMode) {
    setMessages([])
    setConversationId(null)
    setMode(m)
    setInput('')
    setError(null)
  }

  function sendText(text: string, target = { conversationId, mode }) {
    if (!text || disabled || send.isPending) return
    setMessages((m) => [...m, { role: 'user', content: text }])
    setInput('')
    setError(null)
    send.mutate({ text, ...target })
  }

  // Novo escopo (cliente/relatório) ou pedido de conversa nova (startToken).
  useEffect(() => {
    startNew(newMode)
    initializedFor.current = null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  // Entrevista: a IA começa perguntando. Conversa normal: continua a mais recente.
  useEffect(() => {
    if (disabled || !clientId || initializedFor.current === scopeKey) return
    if (startToken > 0 && newMode === 'interview') {
      initializedFor.current = scopeKey
      sendText(INTERVIEW_OPENER, { conversationId: null, mode: 'interview' })
      return
    }
    if (!listLoaded) return
    initializedFor.current = scopeKey
    const target = openConversationId ?? conversations[0]?.id
    if (startToken === 0 && target) open.mutate(target)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, listLoaded, disabled, clientId])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, send.isPending])

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendText(input.trim())
    }
  }

  const current = conversations.find((c) => c.id === conversationId)

  return (
    <div className={`flex flex-col ${className ?? 'h-[calc(100vh-8rem)]'}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {conversations.length > 0 && (
            <select
              value={conversationId ?? ''}
              onChange={(e) => (e.target.value ? open.mutate(e.target.value) : startNew('chat'))}
              disabled={send.isPending}
              className="max-w-[320px] rounded-md border border-slate-300 px-2.5 py-1.5 text-xs focus:border-brand-500 focus:outline-none"
            >
              <option value="">+ Nova conversa</option>
              {conversations.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.mode === 'interview' ? '🎤 ' : ''}
                  {(c.title ?? 'Conversa').slice(0, 50)} · {formatDateTimeBR(c.updatedAt)}
                </option>
              ))}
            </select>
          )}
          {mode === 'interview' && (
            <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
              🎤 Entrevista do dossiê
            </span>
          )}
          {open.isPending && <Spinner className="h-4 w-4" />}
        </div>
        <div className="flex items-center gap-2">
          {current && (
            <button
              type="button"
              onClick={() => {
                if (confirm('Apagar esta conversa? O que a IA salvou no dossiê continua lá.')) remove.mutate(current.id)
              }}
              className="text-xs text-slate-400 hover:text-red-600 hover:underline"
            >
              apagar conversa
            </button>
          )}
          {messages.length > 0 && (
            <ExportPdfButton
              targetRef={scrollRef}
              title={exportTitle}
              subtitle={exportSubtitle}
              disabled={send.isPending}
            />
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 space-y-4 overflow-y-auto rounded-xl border border-slate-200 bg-white p-4"
      >
        {messages.length === 0 && !send.isPending && !open.isPending && (
          <div className="flex h-full items-center justify-center text-center">
            <p className="max-w-md text-sm text-slate-400">{emptyHint}</p>
          </div>
        )}

        {messages.map((m, i) => (
          <MessageBubble key={i} role={m.role} content={m.content} saved={m.saved} />
        ))}

        {send.isPending && (
          <div data-pdf-hide className="flex items-center gap-2 text-sm text-slate-400">
            <Spinner className="h-4 w-4" />
            {mode === 'interview' ? 'Pensando na próxima pergunta…' : 'Consultando os dados…'}
          </div>
        )}
      </div>

      {error && <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="mt-3 flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={disabled}
          placeholder={
            mode === 'interview'
              ? 'Responda à IA… (Enter envia, Shift+Enter quebra linha)'
              : 'Escreva sua pergunta… (Enter envia, Shift+Enter quebra linha)'
          }
          className="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none disabled:bg-slate-50"
        />
        <button
          type="button"
          onClick={() => sendText(input.trim())}
          disabled={!input.trim() || disabled || send.isPending}
          className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {send.isPending ? <Spinner className="h-4 w-4 text-white" /> : 'Enviar'}
        </button>
      </div>
    </div>
  )
}

// data-pdf-msg: o CSS de impressão usa isso para rotular ("Pergunta"/"Resposta
// da IA"), abrir os balões em largura total e trocar o fundo do balão do
// usuário (texto branco sobre fundo da marca sumiria no papel).
function MessageBubble({
  role,
  content,
  saved,
}: {
  role: 'user' | 'assistant'
  content: string
  saved?: string[]
}) {
  if (role === 'user') {
    return (
      <div data-pdf-msg="user" className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-brand-600 px-4 py-2 text-sm text-white">
          {content}
        </div>
      </div>
    )
  }
  return (
    <div data-pdf-msg="assistant" className="flex flex-col items-start gap-1">
      <div className="markdown max-w-[85%] rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-2 text-sm text-slate-800">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
      {saved && saved.length > 0 && (
        <div data-pdf-hide className="max-w-[85%] rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-800">
          📌 Salvo no dossiê: {saved.join(' · ')}
        </div>
      )}
    </div>
  )
}
